import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Loader2, Eye, EyeOff, X, ShieldCheck, User as UserIcon } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(() => new Set());
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/users"); setRows(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email.trim() || !form.password.trim()) { toast.error("E-posta ve şifre zorunludur"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Kullanıcı eklendi");
      setOpen(false); setForm({ email: "", name: "", password: "", role: "user" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısı silinsin mi?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}`, { role }); toast.success("Rol güncellendi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const roleBadge = (role) => role === "admin"
    ? { cls: "bg-amber-100 text-amber-700", label: "Yönetici", Icon: ShieldCheck }
    : role === "viewer"
      ? { cls: "bg-sky-100 text-sky-700", label: "İzleyici", Icon: Eye }
      : { cls: "bg-zinc-100 text-zinc-600", label: "Kullanıcı", Icon: UserIcon };

  const toggleReveal = (id) => setReveal((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <>
      <PageHeader
        title="Kullanıcılar"
        subtitle="Kullanıcıları görüntüle, ekle ve kaldır (yalnızca yönetici)"
        actions={
          <button onClick={() => setOpen(true)} data-testid="add-user-btn"
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors">
            <Plus size={16} /> Yeni Kullanıcı
          </button>
        }
      />
      <div className="p-6">
        <div className="border border-zinc-200 bg-white rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
          <table className="w-full data-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Ad</th><th>E-posta</th><th>Rol</th><th>Şifre</th><th>Oluşturulma</th><th className="text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10 text-zinc-400"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-semibold">{u.name}</td>
                  <td className="mono">{u.email}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {(() => { const b = roleBadge(u.role); return (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold ${b.cls}`}>
                          <b.Icon size={12} />{b.label}
                        </span>
                      ); })()}
                      <select value={u.role === "admin" || u.role === "viewer" ? u.role : "user"}
                        onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === user?.id}
                        data-testid={`role-select-${u.id}`}
                        className="border border-zinc-300 rounded-sm px-1.5 py-1 text-xs bg-white disabled:opacity-40">
                        <option value="user">Kullanıcı</option>
                        <option value="viewer">İzleyici</option>
                        <option value="admin">Yönetici</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="mono" data-testid={`user-pw-${u.id}`}>{reveal.has(u.id) ? (u.password_plain || "—") : "••••••••"}</span>
                      <button onClick={() => toggleReveal(u.id)} data-testid={`toggle-pw-${u.id}`} className="text-zinc-400 hover:text-amber-600">
                        {reveal.has(u.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </td>
                  <td className="mono text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}</td>
                  <td className="text-right">
                    <button onClick={() => del(u)} disabled={u.id === user?.id} data-testid={`delete-user-${u.id}`}
                      className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-zinc-400">{rows.length} kullanıcı</div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onMouseDown={() => setOpen(false)}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="user-dialog">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
              <h3 className="font-head font-bold text-lg">Yeni Kullanıcı</h3>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-600 mb-1 uppercase tracking-wide">Ad Soyad</label>
                <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-600 mb-1 uppercase tracking-wide">E-posta *</label>
                <input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-600 mb-1 uppercase tracking-wide">Şifre *</label>
                <input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-600 mb-1 uppercase tracking-wide">Rol</label>
                <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                  <option value="user">Kullanıcı</option>
                  <option value="viewer">İzleyici</option>
                  <option value="admin">Yönetici</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-200 px-5 py-3">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">İptal</button>
              <button onClick={create} disabled={saving} data-testid="save-user-btn" className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-sm flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="animate-spin" size={15} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
